import React from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@surecompanies/core_components'
import {
  CloseOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'

export enum CardActionKind {
  TERMINATE = 'terminate',
  TERMINATE_AND_NEW = 'terminate-and-new',
  CREATE = 'create',
}

export const CARD_ACTION_LABELS: Record<CardActionKind, string> = {
  [CardActionKind.TERMINATE]: 'Terminate',
  [CardActionKind.TERMINATE_AND_NEW]: 'Terminate & New',
  [CardActionKind.CREATE]: 'Create',
}

const CARD_ACTION_ICONS: Record<CardActionKind, React.ReactNode> = {
  [CardActionKind.TERMINATE]: <MinusCircleOutlined />,
  [CardActionKind.TERMINATE_AND_NEW]: <ReloadOutlined />,
  [CardActionKind.CREATE]: <PlusOutlined />,
}

export interface CardActionBarAction {
  kind: CardActionKind
  onClick: () => void
  /** Gate for this action's own eligibility rule; the in-flight lock is applied here. */
  disabled?: boolean
  danger?: boolean
}

export interface CardActionBarProps {
  selectedCount: number
  /**
   * Which actions this surface offers, in render order. An array rather than a
   * prop per action because *which* actions exist is the one thing that differs
   * between surfaces: the "Associated VCNs" panel offers three, Card Mode only
   * Terminate — the sole card-keyed endpoint, so the only one that acts on the
   * card that was actually ticked.
   */
  actions: CardActionBarAction[]
  /** Which action is in flight, if any — spinner only on that button. */
  activeAction: CardActionKind | null
  onClose: () => void
}

/**
 * Floating action bar for a card selection, shared by every surface that has one
 * (EMS-8827 Part D introduced it for the "Associated VCNs" panel).
 *
 * Rendered through a portal to document.body: ag-grid positions detail rows with
 * CSS transforms, so a position:fixed element nested inside would anchor to the
 * transformed row, not the viewport. That is the reason this is one shared
 * component rather than one per surface.
 */
export default function CardActionBar({
  selectedCount,
  actions,
  activeAction,
  onClose,
}: CardActionBarProps) {
  if (selectedCount === 0) {
    return null
  }
  const isActioning = activeAction !== null
  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.16)',
        border: '1px solid #f0f0f0',
      }}
    >
      <span style={{ fontSize: 13 }}>
        <strong>{selectedCount}</strong> selected
      </span>
      <span style={{ width: 1, height: 24, background: '#f0f0f0' }} />
      {actions.map((action) => (
        <Button
          key={action.kind}
          danger={action.danger}
          icon={CARD_ACTION_ICONS[action.kind]}
          disabled={action.disabled || isActioning}
          loading={activeAction === action.kind}
          onClick={action.onClick}
        >
          {CARD_ACTION_LABELS[action.kind]}
        </Button>
      ))}
      <Button
        type="text"
        icon={<CloseOutlined />}
        disabled={isActioning}
        onClick={onClose}
        aria-label="Clear selection"
      />
    </div>,
    document.body
  )
}
